import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ShareholderTicketFeeList from '@this/domain/shareholder_ticket_fee/shareholder_ticket_fee_list';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Select } from '@this/shared/ui/inputs/select';
import {
  fetchShareholderTicketFeeAirportByCode,
  fetchShareholderTicketFeeAirportsByName
} from './fetch_shareholder_ticket_fee_airports';
import { fetchShareholderTicketFeeOrganizations } from './fetch_shareholder_ticket_fee_organizations';
import {
  fetchShareholderTicketFees,
  type FetchShareholderTicketFeesParams
} from './fetch_shareholder_ticket_fees';
import { ShareholderTicketFeesAirportSelect } from './shareholder_ticket_fees_airport_select';
import type { ShareholderTicketFeesAirportOption } from './shareholder_ticket_fees_airport_select';
import type { ShareholderTicketFeeOrganizationOption } from './shareholder_ticket_fees_organization_select';
import { ShareholderTicketFeesOrganizationSelect } from './shareholder_ticket_fees_organization_select';

function getStyles() {
  return {
    wrapper: {
      padding: '20px'
    } as React.CSSProperties,
    title: {
      display: 'inline-block'
    } as React.CSSProperties,
    searchArea: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#f7f7f7',
      padding: '20px',
      flexWrap: 'wrap',
      marginBottom: '10px'
    } as React.CSSProperties,
    searchForm: {
      display: 'flex',
      alignItems: 'center',
      marginRight: '20px',
      marginBottom: '5px'
    } as React.CSSProperties,
    searchLabel: {
      fontWeight: 'bold',
      marginRight: '5px'
    } as React.CSSProperties,
    table: {
      fontSize: '11px',
      tableLayout: 'auto'
    } as React.CSSProperties
  } as const;
}

const ShareholderTicketFees: React.FC = () => {
  document.title = '株優確認';

  const queryParam = (key: string): string => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get(key) || '';
  };
  const initialSearchParams = useMemo<FetchShareholderTicketFeesParams>(
    () => ({
      organizationId: queryParam('organization_id'),
      fromCode: queryParam('from_code'),
      toCode: queryParam('to_code'),
      airline: queryParam('airline')
    }),
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [shareholderTicketFees, setShareholderTicketFees] = useState<ShareholderTicketFeeList | null>(null);
  const [organizationId, setOrganizationId] = useState<string>(initialSearchParams.organizationId);
  const [fromCode, setFromCode] = useState<string>(initialSearchParams.fromCode);
  const [toCode, setToCode] = useState<string>(initialSearchParams.toCode);
  const [fromAirport, setFromAirport] = useState<ShareholderTicketFeesAirportOption | null>(null);
  const [toAirport, setToAirport] = useState<ShareholderTicketFeesAirportOption | null>(null);
  const [airline, setAirline] = useState<string>(initialSearchParams.airline);
  const [organizationOptions, setOrganizationOptions] = useState<ShareholderTicketFeeOrganizationOption[]>([]);
  const styles = getStyles();

  const getAirportOptions = useCallback((inputValue?: string) => {
    const keyword = inputValue?.trim();
    if (!keyword) return Promise.resolve([]);

    return fetchShareholderTicketFeeAirportsByName(keyword);
  }, []);

  const fetchFees = useCallback(async (params: FetchShareholderTicketFeesParams) => {
    setLoading(true);

    try {
      const [fromAirportOption, toAirportOption, response] = await Promise.all([
        fetchShareholderTicketFeeAirportByCode(params.fromCode),
        fetchShareholderTicketFeeAirportByCode(params.toCode),
        fetchShareholderTicketFees(params)
      ]);

      setFromAirport(fromAirportOption);
      setToAirport(toAirportOption);
      setShareholderTicketFees(new ShareholderTicketFeeList(response.shareholder_ticket_fees));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShareholderTicketFeeOrganizations().then(setOrganizationOptions);
  }, []);

  const handleSearch = useCallback(() => {
    fetchFees({
      organizationId,
      fromCode,
      toCode,
      airline
    });
  }, [airline, fetchFees, fromCode, organizationId, toCode]);

  useEffect(() => {
    fetchFees(initialSearchParams);
  }, [fetchFees, initialSearchParams]);

  return (
    <div style={styles.wrapper}>
      <h2 style={styles.title}>
        <span>株主優待券 確認</span>
      </h2>
      <div style={styles.searchArea}>
        <div style={styles.searchForm}>
          <div style={styles.searchLabel}>法人</div>
          <ShareholderTicketFeesOrganizationSelect
            options={organizationOptions}
            value={organizationId}
            onSelect={(selected: ShareholderTicketFeeOrganizationOption | null) => {
              setOrganizationId(selected ? String(selected.id) : '');
            }}
          />
        </div>
        <div style={styles.searchForm}>
          <div style={styles.searchLabel}>出発地</div>
          <ShareholderTicketFeesAirportSelect
            getOptions={getAirportOptions}
            placeholder="空港を選択"
            value={fromAirport}
            onSelect={selected => {
              setFromCode(selected ? selected.code : '');
              setFromAirport(selected);
            }}
          />
        </div>
        <div style={styles.searchForm}>
          <div style={styles.searchLabel}>到着地</div>
          <ShareholderTicketFeesAirportSelect
            getOptions={getAirportOptions}
            placeholder="空港を選択"
            value={toAirport}
            onSelect={selected => {
              setToCode(selected ? selected.code : '');
              setToAirport(selected);
            }}
          />
        </div>
        <div style={styles.searchForm}>
          <div style={styles.searchLabel}>航空会社</div>
          <Select value={airline} onChange={e => setAirline(e.target.value)} style={{ height: '30px' }}>
            <option value="">すべて</option>
            <option value="ANA">ANA</option>
            <option value="JAL">JAL</option>
            <option value="SFJ">SFJ</option>
          </Select>
        </div>
        <div>
          <Button type="button" onClick={handleSearch}>
            検索
          </Button>
        </div>
      </div>
      <div>
        {loading ? (
          <Loading />
        ) : (
          <table style={styles.table}>
            <tbody>
              <tr>
                <th>航路</th>
                <th>ANA</th>
                <th>JAL</th>
                <th>SFJ</th>
              </tr>
              {shareholderTicketFees?.list.map(fee => (
                <tr key={fee.id}>
                  <td>{fee.routeText}</td>
                  <td>{fee.ana}</td>
                  <td>{fee.jal}</td>
                  <td>{fee.sfj}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ShareholderTicketFees;
